import { Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TableDateCell,
  TomTable,
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { EnhancedContainerUpdateDto } from '../../stores/ContainerJourneyHistoryViewStore'
import { cleanEventName } from '../../utils/container-history-details.utils'

interface Props {
  containerEvents: EnhancedContainerUpdateDto[]
}
export const ContainerJourneyHistoryTable = observer(({ containerEvents }: Props) => {
  const { t } = useTranslate()

  const columns = useMemo<MRT_ColumnDef<EnhancedContainerUpdateDto>[]>(
    () => [
      {
        accessorKey: 'Event.EventType',
        header: t('event', 'Event'),
        accessorFn: row =>
          row.Event?.EventType
            ? t(cleanEventName(row.Event.EventType), row.Event.EventType)
            : t('unknown', 'Unknown'),
        Cell: cell => <Typography variant='subtitle2'>{cell.renderedCellValue}</Typography>,
        size: 150,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'Event.EventTime',
        header: t('timestamp', 'Timestamp'),
        size: 150,
        // ...tomTableColumnFilterPresets.dateMandatory(), // TODO: Fix date filter reading
        Cell: cell => (
          <TableDateCell date={cell.row.original.Event?.EventTime} timeStyle='medium' />
        ),
      },
      {
        accessorKey: 'userName',
        header: t('updatedBy', 'Updated by'),
        size: 150,
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'eventDifferences',
        header: t('description', 'Description'),
        accessorFn: row =>
          row.eventDifferences?.map(d => {
            const previousText = d.previousValue === null ? '' : `${d.previousValue} -> `
            return `${d.title ? t(d.title) : t('unknown', 'Unknown')}: ${previousText}${d.newValue}`
          }) ?? [EmptyIndicator],
        Cell: cells =>
          (cells.renderedCellValue as string[])?.map((value: string, i: number) => (
            <Typography key={i} variant='body2'>
              {value}
            </Typography>
          )),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/container-journey-history',
  )

  const table = useTomTable<EnhancedContainerUpdateDto>(
    {
      columns,
      data: containerEvents,
      enableTopToolbar: false,
      enableRowSelection: false,
      enableExpanding: false,
      enableRowActions: false,
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
